import {
  navMenuHideableWrapper,
  navMenuHideableWrapperAnimation,
  navMenuItemProgressGuide,
  navMenuOpenWrapper,
  navMenuWrapper,
  navMenuTopContent,
} from './navMenu.css';
import { NavMenuList, NavMenuListProps } from './NavMenuList';
import { NavMenuFooter, NavMenuFooterProps } from './NavMenuFooter';
import { NavMenuHeader } from './NavMenuHeader';
import { useEffect, useLayoutEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useBreakpoints } from '@design-system/breakpointsStore';
import { PorgressGuide, PorgressGuideProps } from './PorgressGuide';
import { browserHistory } from '@client/router/compat';

// A null desktop state selects no recipe variant, just like undefined.
const NavMenuHideableWrapper = (p: {
  open: boolean | null;
  children: React.ReactNode;
}) => (
  <div
    className={clsx(
      navMenuHideableWrapper,
      navMenuHideableWrapperAnimation({ open: p.open as boolean | undefined }),
    )}
  >
    {p.children}
  </div>
);

export type NavMenuProps = NavMenuListProps &
  NavMenuFooterProps &
  Partial<PorgressGuideProps> & {
    showProgress?: boolean;
    isAdmin?: boolean;
    avatar?: string;
    displayName?: string;
    topContent?: React.ReactNode;
  };

const blockBodyScroll = () => {
  const scrollY = window.scrollY;
  const bodyStyle = document.body.style;
  bodyStyle.position = 'fixed';
  bodyStyle.top = `-${scrollY}px`;
  bodyStyle.width = '100%';
  bodyStyle.overflowY = 'scroll';

  return () => {
    bodyStyle.position = '';
    bodyStyle.top = '';
    bodyStyle.width = '';
    bodyStyle.overflowY = '';
    window.scrollTo(0, scrollY);
  };
};

export const NavMenu = (p: NavMenuProps) => {
  const [open, setOpen] = useState<boolean | null>(null);
  const isMobile = useBreakpoints((s) => s.mobile);

  useLayoutEffect(() => {
    setOpen(isMobile ? false : null);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    if (open) {
      const unblockBodyScroll = blockBodyScroll();
      const listener = browserHistory.listen(() => setOpen(false));

      return () => {
        unblockBodyScroll();
        listener();
      };
    } else {
      const menuContent = document.querySelector('[role="list"]');
      menuContent?.scrollTo(0, 0);
    }
  }, [isMobile, open]);

  return (
    <nav
      className={clsx(navMenuWrapper, open && navMenuOpenWrapper)}
      data-testid="nav-menu"
    >
      <NavMenuHeader
        isMenuOpen={open !== false}
        onMenuToggle={() => setOpen((p) => (p === null ? false : !p))}
        isAdmin={p.isAdmin}
        avatar={p.avatar}
        displayName={p.displayName}
      />
      {p.showProgress && (
        <div className={navMenuItemProgressGuide}>
          <PorgressGuide progress={p.progress ?? 0} />
        </div>
      )}
      <NavMenuHideableWrapper open={open}>
        {p.topContent && (
          <div className={navMenuTopContent}>{p.topContent}</div>
        )}
        <NavMenuList list={p.list} />
        {p.isAdmin ? null : <NavMenuFooter onLogout={p.onLogout} />}
      </NavMenuHideableWrapper>
    </nav>
  );
};
